import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ButtonPseudo,
  IconLoader,
  PermissionEnum,
  SidePanel,
  SidePanelType,
  SidePanelLoader,
  ResponseError,
  getResponseErrors,
  DeviceSessionSidepanel,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  useLazyGetEmployeeProfileQuery,
  useLazyGetEmpoyeeRolesQuery,
  useLazyGetEmployeeDevicesQuery,
  useLogoutMutation,
  useEndSessionMutation,
  useLazyGetPspPartnerAccountsQuery,
  useLazyGetProfileDeviceSessionsQuery,
} from './store/services';
import { clearDevices, clearDeviceSession } from './store/slices';
import permissionsTree from '../../user-roles/variables';
import getPreparePermissions from '../../user-list/helpers/get-permissions';
import ProfileGeneral from '../../../components/profile/general';
import ProfilePermissions from '../../../components/profile/permissions';
import ProfileEntryAndSecurity from '../../../components/profile/entry-and-security';
import ProfileDevices from '../../../components/profile/devices';
import { Device, Permission } from './types';

import ROUTES from '../../../routes';

import cn from './profile.module.css';

const EmployeeProfile = () => {
  const [devicesPanelVisible, setDevicesPanelVisible] = useState(false);

  const [getProfile, { isLoading }] = useLazyGetEmployeeProfileQuery();
  const [getRoles] = useLazyGetEmpoyeeRolesQuery();
  const [logout, { isLoading: logoutInProgress }] = useLogoutMutation();
  const [
    getEmployeeDevices,
    { isLoading: devicesIsLoading, isFetching: devicesIsFetching },
  ] = useLazyGetEmployeeDevicesQuery();
  const [endSessionId, setEndSessionId] = useState('');
  const [errorList, setErrorList] = useState('');
  const [deviceSessionSidepanelVisible, setDeviceSessionSidepanelVisible] =
    useState(false);
  const [device, setDevice] = useState<Device | undefined>(undefined);

  const [endSession, { isLoading: endSessionInProgress, isSuccess }] =
    useEndSessionMutation();
  const [getPspPArtnersAccounts] = useLazyGetPspPartnerAccountsQuery();
  const [
    getDeviceSessions,
    { isLoading: deviceSessionsIsLoading, isFetching: deviceSessionIsFetching },
  ] = useLazyGetProfileDeviceSessionsQuery();

  const endSessionAction = (id: string) => {
    setEndSessionId(id);

    endSession(id);
  };

  const {
    settings: {
      profile: {
        employeeProfile,
        roles,
        devices,
        devicesPagination,
        deviceSessions,
        deviceSessionsPagination,
      },
    },
    administrationDictionaries: { activePermissions },
  } = useAppSelector((state) => state);

  const dispatch = useAppDispatch();

  const permissions = employeeProfile?.permissions;

  const preparedPermissions = getPreparePermissions(permissionsTree);

  const formattedPermissions: Permission[] = [];

  const onViewSessionClick = (deviceItem: Device) => {
    setDevice(deviceItem);
    setDeviceSessionSidepanelVisible(true);
    setDevicesPanelVisible(false);
  };

  const onCloseDeviceSessionSidepanel = () => {
    setDevice(undefined);
    setDeviceSessionSidepanelVisible(false);
    setDevicesPanelVisible(true);
    dispatch(clearDeviceSession());
  };

  const getProfileAction = useCallback(async () => {
    const response = (await getProfile()) as { error?: ResponseError };

    if (response?.error) {
      getResponseErrors({
        error: response?.error,
        setErrorList,
      });
    }
  }, []);

  Object.keys(preparedPermissions).forEach((item) => {
    const sections = permissions?.filter((permission) =>
      permission.includes('SECTION'),
    );

    if (sections?.find((permission) => permission === item)) {
      if (preparedPermissions[item] && preparedPermissions[item]?.pages) {
        const nextPermissions = { ...preparedPermissions[item] };

        const pages: any[] = [];

        Object.keys(nextPermissions?.pages as any)?.forEach((page) => {
          if (permissions?.find((permission) => permission === page)) {
            if (nextPermissions?.pages && nextPermissions?.pages[page]) {
              const nextPage = { ...nextPermissions?.pages[page] };

              const actions: any[] = [];

              if (nextPage.actions) {
                Object.keys(nextPage.actions).forEach((action) => {
                  if (
                    permissions?.find((permission) => permission === action)
                  ) {
                    if (nextPage.actions && nextPage.actions[action]) {
                      actions.push(nextPage.actions[action]);
                    }
                  }
                });
              }

              nextPage.actions = actions as any;

              pages.unshift(nextPage);
            }
          }
        });

        nextPermissions.pages = pages as any;

        formattedPermissions.push(nextPermissions as any);
      } else {
        formattedPermissions.push(preparedPermissions[item] as any);
      }
    }
  });

  useEffect(() => {
    getProfileAction();
  }, []);

  useEffect(() => {
    if (isSuccess) {
      setEndSessionId('');
    }
  }, [isSuccess]);

  useEffect(() => {
    if (devicesPanelVisible && !devices) {
      getEmployeeDevices('');
    }
  }, [devicesPanelVisible]);

  useEffect(() => {
    if (
      activePermissions?.includes(PermissionEnum.SECTION_ADMINISTRATION) &&
      activePermissions.includes(PermissionEnum.USERS_ROLES_LIST)
    ) {
      getRoles();
    }
  }, [activePermissions]);

  useEffect(() => {
    getPspPArtnersAccounts();
  }, []);

  const navigate = useNavigate();

  const onLogout = async () => {
    const response = await logout();

    if (response) {
      localStorage.removeItem('activePermissions');
      localStorage.removeItem('userId');
      localStorage.removeItem('cardsEnabled');
      localStorage.removeItem('reportLink');

      navigate('/login');
    }
  };

  useEffect(() => {
    let title = 'Settings: Profile';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content;
  let devicesContent = <div />;

  if ((!devicesIsFetching || !devicesIsLoading) && devicesPagination) {
    devicesContent = (
      <ProfileDevices
        devices={devices}
        devicesPagination={devicesPagination}
        devicesIsFetching={devicesIsFetching}
        showMore={(cursor) => getEmployeeDevices(cursor)}
        endSession={endSessionAction}
        endSessionInProgress={endSessionInProgress}
        endSessionId={endSessionId}
        sessionsIsAvailable={activePermissions?.includes(
          PermissionEnum.USERS_DEVICE_SESSIONS_LIST,
        )}
        onViewSessionClick={onViewSessionClick}
      />
    );
  } else if ((devicesIsLoading || devicesIsFetching) && !devicesPagination) {
    devicesContent = <SidePanelLoader />;
  }

  if (isLoading) {
    content = (
      <div className={cn.employeeProfileLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <>
        {!errorList && (
          <>
            <ProfileGeneral
              employeeProfile={employeeProfile}
              roles={roles}
              logout={onLogout}
              havePermissions={formattedPermissions.length > 0}
              logoutInProgress={logoutInProgress}
            />
            <ProfileEntryAndSecurity
              setDevicesPanelVisible={setDevicesPanelVisible}
            />
            <ProfilePermissions permissions={formattedPermissions} />
          </>
        )}
        {errorList && (
          <div className={cn.loadingError}>
            <div className={cn.loadingErrorTitle}>Unable to load data</div>
            <div className={cn.loadingErrorText}>{errorList}</div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className={cn.employeeProfile}>
      {content}
      <DeviceSessionSidepanel
        panelIsOpen={deviceSessionSidepanelVisible}
        handleClose={onCloseDeviceSessionSidepanel}
        deleteSessionIsAvailable={activePermissions?.includes(
          PermissionEnum.EMPLOYEES_SESSIONS_DELETE_ACTION,
        )}
        isLoading={endSessionInProgress}
        onEndSession={() => endSessionAction(device?.id as string)}
        device={device}
        itemId={device?.id}
        deviceId={employeeProfile?.id}
        deviceSessions={deviceSessions}
        activePermissions={activePermissions}
        isFetching={deviceSessionIsFetching}
        deviceSessionsIsLoading={deviceSessionsIsLoading}
        deviceSessionsPagination={deviceSessionsPagination}
        auditLink={ROUTES.SECURITY_LOGS_ITEM}
        getDeviceSessions={getDeviceSessions}
        sessionIsAvailable={activePermissions?.includes(
          PermissionEnum.EMPLOYEES_DEVICE_SESSIONS_LIST,
        )}
      />
      <SidePanel
        type={SidePanelType.AML}
        closeOnClickOutside
        isOpen={devicesPanelVisible}
        onClose={() => {
          setDevicesPanelVisible(false);
          dispatch(clearDevices());
        }}
        title="Devices"
        sidepanelExpandedKey="devices"
        footer={
          <ButtonPseudo
            text="Close"
            onClick={() => {
              setDevicesPanelVisible(false);
              dispatch(clearDevices());
            }}
            bold
          />
        }
      >
        {devicesContent}
      </SidePanel>
    </div>
  );
};

export default EmployeeProfile;
